import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import colors from "core/colors";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "190";
const height = "190";
const viewBox = "0 0 190 190";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({
    sm: { width: 98, height: 98 },
    md: { width: 101, height: 101 },
    lg: { width: 190, height: 190 }
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            fill={colors.secondary.orca}
            fillRule="evenodd"
            d="M31.039 50.115a8.163 8.163 0 10-5.371-.582 2.986 2.986 0 00-.566 1.753v95.33c-2.992.757-5.102 2.59-5.102 4.731 0 2.818 3.655 5.102 8.163 5.102 4.509 0 8.164-2.284 8.164-5.102 0-2.129-2.087-3.953-5.051-4.718v-80.53a88.422 88.422 0 009.285 11.79C50.986 89 67.3 100.266 90.469 100.266c23.091 0 41.609-11.197 54.22-22.165a120.38 120.38 0 0014.087-14.548v83.064c-2.992.757-5.103 2.59-5.103 4.731 0 2.818 3.655 5.102 8.164 5.102 4.508 0 8.163-2.284 8.163-5.102 0-2.129-2.086-3.953-5.051-4.718V51.286c0-.52-.132-1.008-.364-1.434a8.163 8.163 0 10-10.912-7.689 8.166 8.166 0 005.458 7.705 2.986 2.986 0 00-.355 1.418v2.308a70.87 70.87 0 01-.642.922 95.93 95.93 0 01-3.571 4.706 114.339 114.339 0 01-13.811 14.351c-12.028 10.46-29.195 20.692-50.283 20.692-21.014 0-35.85-10.163-45.533-20.481-4.844-5.163-8.365-10.334-10.672-14.213a70.81 70.81 0 01-2.549-4.656c-.176-.355-.323-.66-.44-.908v-2.721a2.99 2.99 0 00-.236-1.171z"
            clipRule="evenodd"
            key="key-0"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "Queue"
});
